/**
 * TIME CALCULATOR ENGINE
 * BPM to milliseconds / Hz calculator for delay, reverb, modulation, and sample timing.
 * No account required. Works anonymously.
 *
 * Persists to localStorage under key "sa-tool-delay".
 */

(function () {
    'use strict';

    const STORAGE_KEY = 'sa-tool-delay';
    const BPM_MIN = 20;
    const BPM_MAX = 350;
    const BEATS_PER_BAR = 4;
    const VALID_MODES = new Set(['delay', 'reverb', 'modulation', 'metric', 'samples', 'frequency']);
    const VALID_SAMPLE_RATES = new Set([44100, 48000, 88200, 96000, 192000]);

    class TimeCalculatorEngine {
        constructor() {
            this.bpm         = 120;
            this.sampleRate  = 48000;
            this.currentMode = 'delay';
            this.tapTimes    = [];

            this.divisions = [
                { label: 'Whole', multiplier: 4      },
                { label: '1/2',   multiplier: 2      },
                { label: '1/4',   multiplier: 1      },
                { label: '1/8',   multiplier: 0.5    },
                { label: '1/16',  multiplier: 0.25   },
                { label: '1/32',  multiplier: 0.125  },
                { label: '1/64',  multiplier: 0.0625 }
            ];

            this.notes = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];
        }

        // ===== PERSISTENCE =====
        saveState() {
            try {
                window.flPreferenceStorage.setItem(STORAGE_KEY, JSON.stringify({
                    bpm:        this.bpm,
                    sampleRate: this.sampleRate,
                    mode:       this.currentMode
                }));
            } catch (_) { /* storage unavailable */ }
        }

        loadState() {
            try {
                const raw = window.flPreferenceStorage.getItem(STORAGE_KEY);
                if (!raw) return;
                const s = JSON.parse(raw);
                if (typeof s.bpm === 'number' && s.bpm >= BPM_MIN && s.bpm <= BPM_MAX) this.bpm = s.bpm;
                if (VALID_SAMPLE_RATES.has(s.sampleRate)) this.sampleRate = s.sampleRate;
                if (VALID_MODES.has(s.mode)) this.currentMode = s.mode;
            } catch (_) { /* corrupted storage */ }
        }

        // ===== MATH =====
        msPerBeat() { return 60000 / this.bpm; }

        getDelayTime(multiplier, type = 'normal') {
            let ms = this.msPerBeat() * multiplier;
            if (type === 'dotted')  ms *= 1.5;
            if (type === 'triplet') ms *= (2 / 3);
            return Math.round(ms * 100) / 100;
        }

        getSamples(ms) {
            return Math.round((ms / 1000) * this.sampleRate);
        }

        getModRate(multiplier) {
            const periodMs = this.msPerBeat() * multiplier;
            const hz       = 1000 / periodMs;
            return { hz: Math.round(hz * 10000) / 10000, periodMs: Math.round(periodMs) };
        }

        // ===== FREQUENCY HELPERS =====
        freqToNote(freq) {
            const noteNum  = 12 * Math.log2(freq / 440) + 69;
            const noteRnd  = Math.round(noteNum);
            const noteIdx  = ((noteRnd % 12) + 12) % 12;
            const octave   = Math.floor(noteRnd / 12) - 1;
            const cents    = Math.round((noteNum - noteRnd) * 100);
            return { note: this.notes[noteIdx] + octave, cents };
        }

        noteToFreq(noteStr) {
            const m = noteStr.match(/^([A-G]#?)(\d)$/);
            if (!m) return 440;
            const idx  = this.notes.indexOf(m[1]);
            const midi = (parseInt(m[2], 10) + 1) * 12 + idx;
            return 440 * Math.pow(2, (midi - 69) / 12);
        }

        // ===== SCREEN READER =====
        announce(msg) {
            const el = document.getElementById('TCLiveRegion');
            if (!el) return;
            el.textContent = '';
            el.textContent = msg;
        }

        // ===== UI BUILDERS =====

        /** Builds or refreshes the main delay time table. */
        buildDelayTable() {
            const grid  = document.getElementById('DelayGrid');
            const table = grid && grid.querySelector('.tc-table');
            if (!table) return;

            table.querySelectorAll('.tc-row:not(.tc-row--header)').forEach(r => r.remove());

            this.divisions.forEach(div => {
                const s = this.getDelayTime(div.multiplier, 'normal');
                const d = this.getDelayTime(div.multiplier, 'dotted');
                const t = this.getDelayTime(div.multiplier, 'triplet');

                const row = document.createElement('div');
                row.className = 'tc-row';
                row.setAttribute('role', 'row');

                const makeCell = (val, ariaLabel) => {
                    const hz    = val > 0 ? Math.round((1000 / val) * 10000) / 10000 : 0;
                    const span  = document.createElement('span');
                    span.className = 'tc-cell tc-value';
                    span.setAttribute('tabindex', '0');
                    span.setAttribute('role', 'gridcell');
                    span.setAttribute('aria-label', `${ariaLabel}: ${val} ms, ${hz} Hz — tap to copy`);
                    span.dataset.value = val;
                    span.innerHTML = `<span class="tc-val-ms">${val}<small> ms</small></span>`
                                   + `<span class="tc-val-hz">${hz}<small> Hz</small></span>`;
                    span.addEventListener('click',   () => this._copyAndFlash(span, val, val + ' ms'));
                    span.addEventListener('keydown', (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            this._copyAndFlash(span, val, val + ' ms');
                        }
                    });
                    return span;
                };

                const noteCell = document.createElement('span');
                noteCell.className = 'tc-cell tc-note';
                noteCell.setAttribute('role', 'rowheader');
                noteCell.textContent = div.label;

                row.appendChild(noteCell);
                row.appendChild(makeCell(s, div.label + ' straight'));
                row.appendChild(makeCell(d, div.label + ' dotted'));
                row.appendChild(makeCell(t, div.label + ' triplet'));
                table.appendChild(row);
            });
        }

        /** Builds or refreshes the modulation rate table. */
        buildModTable() {
            const panel = document.getElementById('ModulationPanel');
            const table = panel && panel.querySelector('.tc-table');
            if (!table) return;

            table.querySelectorAll('.tc-row:not(.tc-row--header)').forEach(r => r.remove());

            this.divisions.forEach(div => {
                const mod = this.getModRate(div.multiplier);
                const row = document.createElement('div');
                row.className = 'tc-row tc-row--3col';
                row.setAttribute('role', 'row');

                const makeCell = (text, copyText, label) => {
                    const span = document.createElement('span');
                    span.className = 'tc-cell tc-value';
                    span.setAttribute('role', 'gridcell');
                    span.setAttribute('tabindex', '0');
                    span.setAttribute('aria-label', label + ' — tap to copy');
                    span.textContent = text;
                    span.addEventListener('click',   () => this._copyAndFlash(span, copyText, text));
                    span.addEventListener('keydown', (e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._copyAndFlash(span, copyText, text); }
                    });
                    return span;
                };

                const noteCell = document.createElement('span');
                noteCell.className = 'tc-cell tc-note';
                noteCell.setAttribute('role', 'rowheader');
                noteCell.textContent = div.label;

                row.appendChild(noteCell);
                row.appendChild(makeCell(mod.hz + ' Hz',   String(mod.hz),      div.label + ' rate'));
                row.appendChild(makeCell(mod.periodMs + ' ms', String(mod.periodMs), div.label + ' period'));
                table.appendChild(row);
            });
        }

        /** Builds or refreshes the sample-length table. */
        buildSamplesTable() {
            const panel = document.getElementById('SamplesPanel');
            const table = panel && panel.querySelector('.tc-table');
            if (!table) return;

            table.querySelectorAll('.tc-row:not(.tc-row--header)').forEach(r => r.remove());

            this.divisions.forEach(div => {
                const ms      = this.getDelayTime(div.multiplier);
                const samples = this.getSamples(ms);
                const row     = document.createElement('div');
                row.className = 'tc-row tc-row--3col';
                row.setAttribute('role', 'row');

                const makeCell = (text, copyText, label) => {
                    const span = document.createElement('span');
                    span.className = 'tc-cell tc-value';
                    span.setAttribute('role', 'gridcell');
                    span.setAttribute('tabindex', '0');
                    span.setAttribute('aria-label', label + ' — tap to copy');
                    span.textContent = text;
                    span.addEventListener('click',   () => this._copyAndFlash(span, copyText, text));
                    span.addEventListener('keydown', (e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._copyAndFlash(span, copyText, text); }
                    });
                    return span;
                };

                const noteCell = document.createElement('span');
                noteCell.className = 'tc-cell tc-note';
                noteCell.setAttribute('role', 'rowheader');
                noteCell.textContent = div.label;

                row.appendChild(noteCell);
                row.appendChild(makeCell(ms + ' ms',                 String(ms),      div.label + ' time'));
                row.appendChild(makeCell(samples.toLocaleString(),   String(samples), div.label + ' samples'));
                table.appendChild(row);
            });
        }

        /** Builds the reference frequency buttons in the Frequency panel. */
        buildNoteFreqGrid() {
            const grid = document.getElementById('NoteFreqGrid');
            if (!grid) return;
            grid.innerHTML = '';
            this.notes.forEach(note => {
                const freq = this.noteToFreq(note + '4');
                const btn  = document.createElement('button');
                btn.className = 'tc-note-btn';
                btn.setAttribute('aria-label', note + '4 — ' + Math.round(freq) + ' Hz');
                btn.innerHTML = note + '<br><small>' + Math.round(freq) + '</small>';
                btn.addEventListener('click', () => {
                    const freqEl = document.getElementById('FreqHz');
                    if (freqEl) freqEl.value = Math.round(freq);
                    this.updateFreqResults(freq);
                });
                grid.appendChild(btn);
            });
        }

        // ===== UPDATE HELPERS =====

        updateReverbPresets() {
            const barMs = this.msPerBeat() * BEATS_PER_BAR;
            const set   = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v; };
            set('ReverbShort',  this.getDelayTime(0.0625) + ' ms');  // 1/64
            set('ReverbMedium', this.getDelayTime(0.125)  + ' ms');  // 1/32
            set('ReverbLong',   this.getDelayTime(0.25)   + ' ms');  // 1/16
            set('Decay1Bar',    (barMs         / 1000).toFixed(2) + ' s');
            set('Decay2Bar',    (barMs * 2     / 1000).toFixed(2) + ' s');
            set('Decay4Bar',    (barMs * 4     / 1000).toFixed(2) + ' s');
        }

        updateSidechainTiming() {
            const set = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v + ' ms'; };
            set('SidechainFast',   Math.round(this.getDelayTime(0.5)));
            set('SidechainMedium', Math.round(this.getDelayTime(1)));
            set('SidechainSlow',   Math.round(this.getDelayTime(2)));
        }

        updateFreqResults(freq) {
            if (!freq || freq <= 0) return;
            const nd = this.freqToNote(freq);
            const set = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v; };
            set('FreqNote',       nd.note);
            set('FreqCents',      (nd.cents >= 0 ? '+' : '') + nd.cents + ' cents');
            set('FreqWavelength', (343 / freq * 100).toFixed(1) + ' cm');
            set('FreqPeriod',     (1000 / freq).toFixed(3) + ' ms');
        }

        calculateMetricBpm() {
            const from = parseFloat(document.getElementById('MetricFrom')?.value || '1');
            const to   = parseFloat(document.getElementById('MetricTo')?.value   || '1');
            if (!from) return;
            const newBpm   = this.bpm * (to / from);
            const resultEl = document.getElementById('MetricResultBpm');
            if (resultEl) {
                resultEl.textContent = newBpm.toFixed(2);
                resultEl.classList.remove('pulse');
                void resultEl.offsetWidth; // force reflow to re-trigger animation
                resultEl.classList.add('pulse');
            }
        }

        /** Re-render every live output for the current BPM. */
        updateAll() {
            this.buildDelayTable();
            this.updateReverbPresets();
            this.updateSidechainTiming();
            this.calculateMetricBpm();
            if (this.currentMode === 'modulation') this.buildModTable();
            if (this.currentMode === 'samples')    this.buildSamplesTable();
        }

        // ===== COPY =====

        _copyAndFlash(el, numericValue, displayText) {
            const text = String(numericValue);

            const doFlash = () => {
                if (el) {
                    el.classList.add('is-copied');
                    clearTimeout(el._flashTimer);
                    el._flashTimer = setTimeout(() => el.classList.remove('is-copied'), 1200);
                }
                this._showCopyStatus(displayText || text);
                this.announce('Copied ' + (displayText || text));
            };

            const showFailure = () => {
                const message = 'Copy failed. Select the value and copy it manually.';
                const status = document.getElementById('CopyStatus');
                if (status) {
                    status.textContent = message;
                    status.classList.remove('is-copied');
                    clearTimeout(this._copyTimer);
                    this._copyTimer = setTimeout(() => {
                        status.textContent = 'Tap any value to copy';
                    }, 3000);
                }
                this.announce(message);
            };

            const tryFallback = () => {
                if (this._fallbackCopy(text)) doFlash();
                else showFailure();
            };

            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(doFlash).catch(tryFallback);
            } else {
                tryFallback();
            }
        }

        _fallbackCopy(text) {
            let ta = null;
            try {
                ta            = document.createElement('textarea');
                ta.value      = text;
                ta.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
                document.body.appendChild(ta);
                ta.select();
                return document.execCommand('copy') === true;
            } catch (_) {
                return false;
            } finally {
                if (ta) ta.remove();
            }
        }

        _showCopyStatus(displayText) {
            const status = document.getElementById('CopyStatus');
            if (!status) return;
            status.textContent = 'Copied ' + displayText;
            status.classList.add('is-copied');
            clearTimeout(this._copyTimer);
            this._copyTimer = setTimeout(() => {
                status.textContent = 'Tap any value to copy';
                status.classList.remove('is-copied');
            }, 2000);
        }

        // ===== TAP TEMPO =====
        tap() {
            const now    = Date.now();
            const tapBtn = document.getElementById('TapTempoBtn');

            // Flash button
            if (tapBtn) {
                tapBtn.classList.add('is-tapped');
                clearTimeout(this._tapFlashTimer);
                this._tapFlashTimer = setTimeout(() => tapBtn.classList.remove('is-tapped'), 150);
            }

            // Reset if gap is too long
            if (this.tapTimes.length > 0 && now - this.tapTimes[this.tapTimes.length - 1] > 2500) {
                this.tapTimes = [];
            }

            this.tapTimes.push(now);
            if (this.tapTimes.length > 8) this.tapTimes.shift();

            if (this.tapTimes.length >= 2) {
                let total = 0;
                for (let i = 1; i < this.tapTimes.length; i++) {
                    total += this.tapTimes[i] - this.tapTimes[i - 1];
                }
                this.bpm = Math.max(BPM_MIN, Math.min(BPM_MAX, Math.round(60000 / (total / (this.tapTimes.length - 1)))));
                this._syncBpmInput();
                this.announce('BPM set to ' + this.bpm);
            } else {
                this.announce('Tap again to set BPM');
            }
        }

        // ===== PANELS =====
        showModePanel() {
            const map = {
                delay:      'DelayGrid',
                reverb:     'ReverbPanel',
                modulation: 'ModulationPanel',
                metric:     'MetricPanel',
                samples:    'SamplesPanel',
                frequency:  'FrequencyPanel'
            };
            Object.entries(map).forEach(([mode, id]) => {
                const el = document.getElementById(id);
                if (!el) return;
                const visible = (mode === this.currentMode);
                el.style.display = visible ? '' : 'none';
                el.setAttribute('aria-hidden', visible ? 'false' : 'true');
            });
            if (this.currentMode === 'modulation') this.buildModTable();
            if (this.currentMode === 'samples')    this.buildSamplesTable();
        }

        // ===== KEYBOARD =====
        handleKeydown(e) {
            const activeTab = e.target.closest('[role="tab"][data-mode]');
            if (activeTab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                e.preventDefault();
                const tabs = Array.from(document.querySelectorAll('[role="tab"][data-mode]'));
                const currentIndex = tabs.indexOf(activeTab);
                let nextIndex = currentIndex;
                if (e.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
                if (e.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
                if (e.key === 'Home') nextIndex = 0;
                if (e.key === 'End') nextIndex = tabs.length - 1;
                tabs[nextIndex].focus();
                tabs[nextIndex].click();
                return;
            }

            if (e.target.closest('input, select, textarea, button, a, [contenteditable="true"], [role="button"]')) return;

            if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
                e.preventDefault();
                this.bpm = Math.min(BPM_MAX, this.bpm + 1);
                this._syncBpmInput();
            } else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
                e.preventDefault();
                this.bpm = Math.max(BPM_MIN, this.bpm - 1);
                this._syncBpmInput();
            }
        }

        _syncBpmInput() {
            const el = document.getElementById('BpmInput');
            if (el) el.value = this.bpm;
            this.updateAll();
            this.saveState();
        }

        // ===== EVENTS =====
        bindEvents() {
            // BPM typed
            document.getElementById('BpmInput')?.addEventListener('input', (e) => {
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v) && v >= BPM_MIN && v <= BPM_MAX) {
                    this.bpm = v;
                    this.updateAll();
                    this.saveState();
                }
            });

            // +/- buttons: immediate pointer step, then repeat after a deliberate hold.
            // Keyboard activation still arrives through click with event.detail === 0.
            const bindBpmAdjuster = (id, dir) => {
                const button = document.getElementById(id);
                if (!button) return;

                let repeatDelay = null;
                let repeatTimer = null;
                let pointerStepHandled = false;

                const step = () => {
                    this.bpm = dir > 0
                        ? Math.min(BPM_MAX, this.bpm + 1)
                        : Math.max(BPM_MIN, this.bpm - 1);
                    this._syncBpmInput();
                };

                const stopRepeat = () => {
                    clearTimeout(repeatDelay);
                    clearInterval(repeatTimer);
                    repeatDelay = null;
                    repeatTimer = null;
                };

                button.addEventListener('pointerdown', (e) => {
                    if (e.pointerType === 'mouse' && e.button !== 0) return;
                    e.preventDefault();
                    pointerStepHandled = true;
                    step();
                    if (button.setPointerCapture) button.setPointerCapture(e.pointerId);
                    repeatDelay = setTimeout(() => {
                        step();
                        repeatTimer = setInterval(step, 80);
                    }, 350);
                });

                button.addEventListener('pointerup', stopRepeat);
                button.addEventListener('pointercancel', () => {
                    stopRepeat();
                    pointerStepHandled = false;
                });
                button.addEventListener('lostpointercapture', stopRepeat);
                button.addEventListener('click', (e) => {
                    if (pointerStepHandled && e.detail > 0) {
                        pointerStepHandled = false;
                        e.preventDefault();
                        return;
                    }
                    step();
                });
            };

            bindBpmAdjuster('BpmMinus', -1);
            bindBpmAdjuster('BpmPlus', 1);

            // Tap tempo
            document.getElementById('TapTempoBtn')?.addEventListener('click', () => this.tap());

            // Mode tabs
            document.querySelectorAll('[data-mode]').forEach(tab => {
                tab.addEventListener('click', () => {
                    document.querySelectorAll('[data-mode]').forEach(t => {
                        t.classList.remove('active');
                        t.setAttribute('aria-selected', 'false');
                        t.setAttribute('tabindex', '-1');
                    });
                    tab.classList.add('active');
                    tab.setAttribute('aria-selected', 'true');
                    tab.setAttribute('tabindex', '0');
                    this.currentMode = tab.dataset.mode;
                    this.showModePanel();
                    this.saveState();
                });
            });

            // Sample rate
            document.getElementById('SampleRateSelect')?.addEventListener('change', (e) => {
                this.sampleRate = parseInt(e.target.value, 10);
                this.buildSamplesTable();
                this.saveState();
            });

            // Frequency
            document.getElementById('FreqHz')?.addEventListener('input', (e) => {
                const f = parseFloat(e.target.value);
                if (f > 0) this.updateFreqResults(f);
            });

            // Metric modulation
            document.getElementById('MetricFrom')?.addEventListener('change', () => this.calculateMetricBpm());
            document.getElementById('MetricTo')?.addEventListener('change',   () => this.calculateMetricBpm());
            document.getElementById('BtnApplyMetric')?.addEventListener('click', () => {
                const el  = document.getElementById('MetricResultBpm');
                if (!el) return;
                const v   = parseFloat(el.textContent);
                if (isNaN(v)) return;
                this.bpm  = Math.max(BPM_MIN, Math.min(BPM_MAX, Math.round(v)));
                this._syncBpmInput();
                this.announce('Tempo updated to ' + this.bpm + ' BPM');
            });

            // Quick copy buttons
            document.querySelectorAll('.tc-copy-btn').forEach(btn => {
                btn.addEventListener('click', () => {
                    const note = btn.dataset.note;
                    let mult = 1;
                    if (note.startsWith('1/8'))  mult = 0.5;
                    else if (note.startsWith('1/16')) mult = 0.25;
                    const type = note.endsWith('d') ? 'dotted' : note.endsWith('t') ? 'triplet' : 'normal';
                    const val  = this.getDelayTime(mult, type);
                    this._copyAndFlash(null, val, val + ' ms');
                });
            });

            // Custom Hz finder
            document.getElementById('TargetHz')?.addEventListener('input', (e) => {
                const hz = parseFloat(e.target.value);
                if (!hz || hz <= 0) return;
                const pm    = 1000 / hz;
                const beats = pm / this.msPerBeat();
                const el    = document.getElementById('HzResult');
                if (el) el.textContent = Math.round(pm) + ' ms / ' + beats.toFixed(2) + ' beats';
            });

            // Arrow keys adjust BPM
            document.addEventListener('keydown', (e) => this.handleKeydown(e));

            // Stop audio / clean up on page hide (Audio rule compliance; no audio nodes in this tool)
            document.addEventListener('visibilitychange', () => {
                if (document.hidden) {
                    // nothing to stop — no AudioContext in this tool
                }
            });
        }

        // ===== INIT =====
        init() {
            this.loadState();

            const bpmInput = document.getElementById('BpmInput');
            if (bpmInput) bpmInput.value = this.bpm;

            const srSelect = document.getElementById('SampleRateSelect');
            if (srSelect) srSelect.value = this.sampleRate;

            document.querySelectorAll('[data-mode]').forEach(tab => {
                const active = tab.dataset.mode === this.currentMode;
                tab.classList.toggle('active', active);
                tab.setAttribute('aria-selected', active ? 'true' : 'false');
                tab.setAttribute('tabindex', active ? '0' : '-1');
            });

            this.showModePanel();
            this.updateAll();
            this.buildNoteFreqGrid();
            this.updateFreqResults(440);
            this.bindEvents();

            const loader = document.getElementById('DelayLoader');
            if (loader) {
                loader.setAttribute('aria-hidden', 'true');
                loader.style.display = 'none';
            }
        }
    }

    document.addEventListener('DOMContentLoaded', () => {
        window.flRunWhenPrivacyReady(() => {
            const engine = new TimeCalculatorEngine();
            engine.init();
            window.TimeCalculator = engine; // public handle for console debugging
        });
    });

})();
